import { logout } from '../services/auth';
import { clearBrowserData } from '../services/api';

// Global flag to prevent multiple logout attempts
let isLoggingOut = false;

/**
 * Handle authentication errors and automatically logout user if token is invalid
 * @param error - The error object from API calls
 * @param redirectToLogin - Whether to redirect to login page after logout
 */
export const handleAuthError = async (error: any, redirectToLogin: boolean = true): Promise<void> => {
  // Check if this is an authentication error
  const isAuthError = 
    error?.message?.includes('Unauthorized') ||
    error?.message?.includes('Invalid token') ||
    error?.message?.includes('Token expired') ||
    error?.status === 401 ||
    error?.response?.status === 401;

  if (isAuthError && !isLoggingOut) {
    isLoggingOut = true;
    
    try {
      console.log('Authentication error detected, logging out user...');
      
      // Clear all browser data
      clearBrowserData();
      
      // Attempt to call logout API (but don't fail if it doesn't work)
      try {
        await logout();
      } catch (logoutError) {
        console.warn('Logout API call failed, but continuing with local cleanup:', logoutError);
      }
      
      // Reload the page to redirect to login
      if (redirectToLogin) {
        window.location.reload();
      }
    } catch (cleanupError) {
      console.error('Error during logout cleanup:', cleanupError);
      // Force reload even if cleanup fails
      if (redirectToLogin) {
        window.location.reload();
      }
    } finally {
      // Reset the flag after a delay to allow for page reload
      setTimeout(() => {
        isLoggingOut = false;
      }, 1000);
    }
  }
};

/**
 * Create a wrapper for API calls that automatically handles auth errors
 * @param apiCall - The API function to wrap
 * @returns Wrapped function that handles auth errors
 */
export const withAuthErrorHandling = <T extends any[], R>(
  apiCall: (...args: T) => Promise<R>
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await apiCall(...args);
    } catch (error) {
      await handleAuthError(error);
      throw error; // Re-throw the error for the calling code to handle if needed
    }
  };
}; 